import { useCallback, useMemo } from "react";
import { useQueries, type UseQueryResult } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/machines/api/config.ts";
import getEvents, { type IGetEventsInput } from "@/machines/api/requests/events.requests.ts";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore.ts";
import type { MachineEvent } from "@/machines/types/event.ts";
import type { ApiResponse } from "@/shared/types/api.ts";
import type { Machine } from "@/machines/types/machine.ts";

/** Normalized row for the location Events panel. */
export interface MapEventRow {
  id: string;
  /** Event uuid — resolves the detail (images) for the on-map event card. */
  uuid: string;
  deviceMxid: string;
  machineName: string;
  state: string;
  startTime: Date;
  durationSeconds: number;
  productive: boolean;
  color: string;
  /**
   * Timeline truck-cycle rows have no unified event detail (`uuid` is the cycle
   * UUID, which `/api/events/{uuid}/` doesn't serve), so the on-map card skips
   * the image fetch for them.
   */
  isTruckCycle?: boolean;
}

export interface LocationEvents {
  events: MapEventRow[];
  isLoading: boolean;
  isError: boolean;
}

const EVENTS_PER_MACHINE = 200;

/**
 * Location-aggregate machine-event feed — one `getEvents` query per machine,
 * tagged with the machine name, merged and sorted by start time desc. Capped at
 * {@link EVENTS_PER_MACHINE} per machine to keep the panel light.
 *
 * `enabled` gates every per-machine query: the only consumer is the Events
 * panel, so callers pass `false` while it's closed to skip the N×{@link
 * EVENTS_PER_MACHINE} fetch entirely.
 */
export const useLocationEvents = (
  machines: Machine[],
  enabled: boolean = true,
): LocationEvents => {
  const { value } = useDateTimeRangeStore();
  const devices = useMemo(
    () =>
      machines
        .filter((m) => m.device_mxid)
        .map((m) => ({ device_mxid: m.device_mxid, name: m.name })),
    [machines],
  );

  // Merge/sort inside `combine` (stable ref via `useCallback`) so the returned
  // object keeps a stable reference when the underlying query results are
  // unchanged — query-core memoizes it via `replaceEqualDeep`, unlike a trailing
  // `useMemo` fed the fresh array `useQueries` returns every render.
  const combine = useCallback(
    (results: UseQueryResult<ApiResponse<MachineEvent[]>>[]): LocationEvents => {
      const events: MapEventRow[] = [];
      let isLoading = false;
      let isError = false;
      results.forEach((q, i) => {
        if (q.status === "pending") isLoading = true;
        if (q.status === "error") isError = true;
        const name = devices[i]?.name ?? "—";
        (q.data?.data ?? []).forEach((e) => {
          events.push({
            id: String(e.id ?? e.uuid),
            uuid: e.uuid,
            deviceMxid: devices[i]?.device_mxid ?? "",
            machineName: name,
            state: String(e.state ?? "—"),
            startTime: new Date(e.startTime),
            durationSeconds: e.durationSeconds ?? 0,
            productive: !!e.productive,
            color: e.color || "#3b82f6",
          });
        });
      });
      events.sort((a, b) => b.startTime.getTime() - a.startTime.getTime());
      return { events, isLoading, isError };
    },
    [devices],
  );

  return useQueries({
    queries: devices.map(({ device_mxid }) => ({
      queryKey: [
        QUERY_KEYS.EVENTS,
        { device_mxid, start_time: value.fromDate, end_time: value.toDate, map: true },
      ],
      queryFn: ({ signal }: { signal: AbortSignal }): Promise<ApiResponse<MachineEvent[]>> =>
        getEvents(
          {
            device_mxid,
            start_time: value.fromDate,
            end_time: value.toDate,
            page: 1,
            itemsPerPage: EVENTS_PER_MACHINE,
          } as IGetEventsInput,
          signal,
        ),
      enabled: enabled && !!device_mxid,
    })),
    combine,
  });
};
